import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContadorNaoLidas, useNotificacoes } from "../../hooks/notificacoes/useNotificacoes";
import { infoNotificacao } from "../../utils/notificacoes/tipoNotificacao";
import { tempoDecorrido } from "../../utils/solicitacoes/formatters";
import { processoInfo, etapaLabel } from "../../utils/solicitacoes/processoRegistry";
import { useApp } from "../../context/appContext";
import { APLICATIVOS } from "../../config/applications";

/**
 * Sino de notificações do navbar — badge com não lidas (polling) + dropdown com as últimas 10.
 * Notificações são um recurso do MyDash Solicitações (API protegida no backend por
 * APP_MYDASH_SOLICITACOES); quem não tem esse acesso nem vê o sino nem gera chamadas de API.
 */
export default function NotificacaoBell() {
    const navigate = useNavigate();
    const [aberto, setAberto] = useState(false);
    const containerRef = useRef(null);
    const { aplicativosPermitidos } = useApp();

    const temAcesso = aplicativosPermitidos.some(app => app.key === APLICATIVOS.MYDASH_SOLICITACOES);

    const { quantidade, recarregarContador } = useContadorNaoLidas(temAcesso);
    const { notificacoes, marcarComoLida, marcarTodasComoLidas } = useNotificacoes({ limite: 10 }, temAcesso && aberto);

    useEffect(() => {
        function aoClicarFora(e) {
            if (aberto && containerRef.current && !containerRef.current.contains(e.target)) {
                setAberto(false);
            }
        }
        document.addEventListener("mousedown", aoClicarFora);
        return () => document.removeEventListener("mousedown", aoClicarFora);
    }, [aberto]);

    if (!temAcesso) return null;

    async function abrirNotificacao(notificacao) {
        if (!notificacao.lida) {
            await marcarComoLida(notificacao.id);
            recarregarContador();
        }
        setAberto(false);
        if (notificacao.solicitacaoId) {
            navigate(`/solicitacoes/${notificacao.solicitacaoId}`);
        }
    }

    async function handleMarcarTodas(e) {
        e.stopPropagation();
        await marcarTodasComoLidas();
        recarregarContador();
    }

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <button
                onClick={() => setAberto(a => !a)}
                className={`nav-btn nb-bell-btn${aberto ? " active" : ""}`}
                aria-label="Notificações"
            >
                <i className="fa fa-bell nav-icon" />
                {quantidade > 0 && <span className="nb-badge">{quantidade > 99 ? "99+" : quantidade}</span>}
            </button>

            {aberto && (
                <div className="dropdown-panel nb-panel max-h-[85vh] overflow-y-auto">
                    <div className="nb-header">
                        <span>Notificações</span>
                        {quantidade > 0 && (
                            <button className="nb-link" onClick={handleMarcarTodas}>Marcar todas como lidas</button>
                        )}
                    </div>

                    {notificacoes.length === 0 ? (
                        <p className="nb-vazio">Nenhuma notificação ainda.</p>
                    ) : (
                        notificacoes.map((n, idx) => {
                            const info = infoNotificacao(n.tipo);
                            const processoLabel = n.tipoProcesso ? (processoInfo(n.tipoProcesso)?.label || n.tipoProcesso) : null;
                            const etapaTxt = n.etapaCodigo ? (etapaLabel(n.tipoProcesso, n.etapaCodigo) || n.etapaCodigo) : null;
                            return (
                                <div key={n.id}>
                                    {idx > 0 && <div className="dd-divider" />}
                                    <button
                                        className={`nb-item${!n.lida ? " nb-item-naolida" : ""}`}
                                        onClick={() => abrirNotificacao(n)}
                                    >
                                        <span className={`nb-dot nb-dot-${info.cor}`} />
                                        <div className="nb-item-conteudo">
                                            <span className="nb-item-titulo">{n.titulo}</span>
                                            {n.solicitacaoNumero && (
                                                <span className="nb-item-sub">Solicitação {n.solicitacaoNumero}</span>
                                            )}
                                            {(etapaTxt || processoLabel) && (
                                                <span className="nb-item-sub">
                                                    {[etapaTxt, processoLabel].filter(Boolean).join(" · ")}
                                                </span>
                                            )}
                                            <span className="nb-item-tempo">{tempoDecorrido(n.dataHora)}</span>
                                        </div>
                                    </button>
                                </div>
                            );
                        })
                    )}

                    <div className="dd-divider" />
                    <button
                        className="nb-ver-todas"
                        onClick={() => { setAberto(false); navigate("/solicitacoes/notificacoes"); }}
                    >
                        Ver todas as notificações
                    </button>
                </div>
            )}
        </div>
    );
}
